import {
  createContext,
  Dispatch,
  useContext,
  useEffect,
  useReducer,
  useRef,
} from 'react'
import { AppService } from '../services/AppService'
import { AppInfo } from '../definitions/api.types'

import * as amplitude from '@amplitude/analytics-browser'
import { sessionReplayPlugin } from '@amplitude/plugin-session-replay-browser'

export const REFRESH_APP_INFO = 'refreshAppInfo'
export const RECHECK_APP_INFO = 'recheckAppInfo'
export const SET_APP_INFO = 'setAppInfo'
export const SET_WAFFLE_FLAGS = 'waffleFlags'
export const SHOW_ADD_SERVICE = 'showAddService'
export const CLOSE_RIGHT_SIDEBAR = 'closeRightSidebar'
export const SHOW_RIGHT_SIDEBAR = 'showRightSidebar'
export const SET_DASHBOARD_ANIMATED = 'setDashboardAnimated'

export interface AppProviderState {
  rightSidebar: boolean
  waffleFlags: string[]
  isAddServiceShown: boolean
  appInfo?: AppInfo | undefined
  refetchTrigger: number
  recheckTrigger: number
  dashboardAnimated: boolean
  isGoogleMapsLoaded: boolean
}

export const reducer = (state: AppProviderState, action: any) => {
  switch (action.type) {
    case SHOW_RIGHT_SIDEBAR:
      return {
        ...state,
        rightSidebar: true,
      }

    case CLOSE_RIGHT_SIDEBAR:
      return {
        ...state,
        rightSidebar: false,
      }

    case SET_WAFFLE_FLAGS:
      return {
        ...state,
        waffleFlags: action.payload,
      }

    case SHOW_ADD_SERVICE:
      return {
        ...state,
        isAddServiceShown: action.payload,
      }

    case SET_APP_INFO: {
      const enabledFlags: string[] = []

      if (action.payload?.flags) {
        Object.entries(action.payload.flags).forEach(([key, value]) => {
          if (!!value) enabledFlags.push(key)
        })
      }

      return {
        ...state,
        appInfo: action.payload,
        waffleFlags: enabledFlags,
      }
    }

    case REFRESH_APP_INFO:
      return {
        ...state,
        refetchTrigger: state.refetchTrigger + 1,
      }

    case RECHECK_APP_INFO:
      return {
        ...state,
        recheckTrigger: state.recheckTrigger + 1,
      }

    case SET_DASHBOARD_ANIMATED:
      return {
        ...state,
        dashboardAnimated: action.payload,
      }

    default:
      return state
  }
}

export const initialState = {
  rightSidebar: false,
  waffleFlags: [],
  isAddServiceShown: false,
  isGoogleMapsLoaded: false,
  appInfo: undefined,
  refetchTrigger: 0,
  recheckTrigger: 0,
  dashboardAnimated: false,
}

export const AppContext = createContext<
  [AppProviderState, Dispatch<any>] | undefined
>(undefined)
AppContext.displayName = 'AppContext'

const app = new AppService()

const initAnalytics = (appInfo: AppInfo) => {
  // check if user has analytics enabled
  if (!appInfo.analytics?.enabled) {
    return
  }

  const analyticsApiKey = import.meta.env.VITE_AMPLITUDE_API_KEY
  if (!analyticsApiKey) {
    console.error('Amplitude API key is not defined') // eslint-disable-line
    return
  }

  // Create and Install Session Replay Plugin
  const sessionReplayTracking = sessionReplayPlugin()
  amplitude.add(sessionReplayTracking)
  amplitude.init(analyticsApiKey, appInfo.analytics.identifier, {
    serverZone: 'EU',
    fetchRemoteConfig: false,
    autocapture: true,
    minIdLength: 1,
  })
}

function AppProvider(props: any) {
  const [state, dispatch] = useReducer(reducer, initialState)

  const isRecheckInFlight = useRef(false)

  const fetchAppInfo = async () => {
    dispatch({ type: SET_WAFFLE_FLAGS, payload: [] })
    const appInfo = await app.getAppInfo()
    dispatch({ type: SET_APP_INFO, payload: appInfo.data })
  }

  const recheckAppInfo = async () => {
    if (isRecheckInFlight.current) return
    isRecheckInFlight.current = true
    try {
      const appInfo = await app.getAppInfo()
      dispatch({ type: SET_APP_INFO, payload: appInfo.data })
    } catch {
      return
    } finally {
      isRecheckInFlight.current = false
    }
  }

  useEffect(() => {
    fetchAppInfo()
  }, [])

  useEffect(() => {
    if (state.refetchTrigger) {
      fetchAppInfo()
    }
  }, [state.refetchTrigger])

  useEffect(() => {
    if (state.recheckTrigger) {
      recheckAppInfo()
    }
  }, [state.recheckTrigger])

  const isEmailUnverified = state.appInfo?.email_verified === false

  useEffect(() => {
    if (!isEmailUnverified) return

    const onForeground = () => {
      if (document.visibilityState === 'visible') recheckAppInfo()
    }

    document.addEventListener('visibilitychange', onForeground)
    window.addEventListener('focus', onForeground)

    return () => {
      document.removeEventListener('visibilitychange', onForeground)
      window.removeEventListener('focus', onForeground)
    }
  }, [isEmailUnverified])

  useEffect(() => {
    if (state.appInfo && state.waffleFlags?.includes('user_analytics')) {
      initAnalytics(state.appInfo)
    }
  }, [state.appInfo, state.waffleFlags])

  return <AppContext.Provider value={[state, dispatch]} {...props} />
}

const useApp = (): [AppProviderState, Dispatch<any>] => {
  const context = useContext(AppContext)
  if (context === undefined) {
    return [initialState, () => {}]
  }

  return context
}

export { AppProvider, useApp }
